import { createSlice } from "@reduxjs/toolkit";
import { apiCaller } from "../../api/apiCaller";
import { playerapi } from "../../sharedfiles/EndpointConfig";

/**
 * Player Insights — one behavioural profile for the player currently open in
 * the back office.
 *
 * Served by UPS at GET /player/{userId}/insights, which builds the profile
 * from that brand's own data. The operator's existing Authorisation +
 * MerchantId headers (added by apiCaller) are what scope it: UPS resolves the
 * tenant schema from the token, so an operator can only ever read players in
 * a brand they hold a key for.
 *
 * Payload contract is the snake_case PlayerProfile from the design brief
 * (Appendix A/B) — the same shape ./data/mockPlayerProfile.js reproduces.
 */
export const playerInsightsSlice = createSlice({
  name: "playerInsights",
  initialState: {
    profile: null,
    loading: false,
    error: null,
    /** Whose profile `profile` holds, so a stale player's data can't linger. */
    loadedFor: null,
  },
  reducers: {
    setProfile: (state, action) => {
      state.profile = action.payload.profile;
      state.loadedFor = action.payload.userId;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.profile = null;
    },
    resetPlayerInsights: (state) => {
      state.profile = null;
      state.error = null;
      state.loadedFor = null;
    },
  },
});

export const { setProfile, setLoading, setError, resetPlayerInsights } =
  playerInsightsSlice.actions;

export const getPlayerInsights = (userId) => (dispatch) => {
  if (userId === undefined || userId === null || userId === "") {
    dispatch(resetPlayerInsights());
    return;
  }

  // Clear first: this tab is reached by navigating between customers, and
  // showing the previous player's profile under a new player's name while the
  // request is in flight would be worse than showing nothing.
  dispatch(resetPlayerInsights());
  dispatch(setLoading(true));

  apiCaller(playerapi.getPlayerInsights(userId), "GET")
    .then((res) => {
      // apiCaller resolves the CommonResponse envelope: { status, data, msg }
      // — note `msg`, not `message`, which is what UPS's CommonResponse emits.
      if (res && res.status === 200 && res.data) {
        dispatch(setProfile({ profile: res.data, userId }));
      } else {
        dispatch(setError(res?.msg || "Player insights are unavailable."));
      }
    })
    .catch((err) => {
      dispatch(
        setError(
          err?.response?.msg ||
            err?.response?.message ||
            "Player insights could not be loaded."
        )
      );
    })
    .finally(() => {
      dispatch(setLoading(false));
    });
};

export default playerInsightsSlice.reducer;
